import type { Program } from "../types";

export interface ProgramConfig {
  program: Program;
  programName: string;
  programShort: string;
  primaryColor: string;
  logoPath: string;
  defaultGender: "Male" | "Female";
  publicUrl: string;
  applicationDeadline: string;
  sessionStartDate: string;
  sessionEndDate: string;
  amendmentsCloseDate: string;
  // Deadline for confirming a sponsor in the post-submission step (June 1).
  sponsorDeadline: string;
  draftCleanupDaysAfterDeadline: string | "NEVER";
  programInfoUrl: string;
  supportEmail: string;
  adminNotificationEmail: string;
  sessionYear: number;
}

export interface ServerConfig extends ProgramConfig {
  azureTenantId: string;
  azureClientId: string;
  azureClientSecret: string;
  sharePointSiteId: string;
  sharePointDriveId: string;
  sharePointApplicationsFolderPath: string;
  sharePointDraftsFolderPath: string;
  sharePointTrackingExcelPath: string;
  sharePointDraftsListName: string;
  // Preferred address for the Drafts list; falls back to the display name above.
  sharePointDraftsListId: string;
  // Round 17 — per-program append-only audit List (MDBS-Audit / MDGS-Audit).
  // Provisioned once by the owner; empty disables audit writes (logged clearly).
  sharePointAuditListId: string;
  senderEmail: string;
  port: number;
  sessionSecret: string;
  maxUploadMb: number;
  // Base URL used to build links in emails (magic-link resume, sponsor step).
  appBaseUrl: string;
  // Shared secret protecting admin endpoints (roster export, manual job triggers).
  adminExportKey: string;
}

export interface PublicConfig {
  program: Program;
  programName: string;
  programShort: string;
  primaryColor: string;
  defaultGender: "Male" | "Female";
  applicationDeadline: string;
  sessionStartDate: string;
  sessionEndDate: string;
  amendmentsCloseDate: string;
  sponsorDeadline: string;
  programInfoUrl: string;
  supportEmail: string;
  sessionYear: number;
  maxUploadMb: number;
  publicUrl: string;
}

export function isLateDeadline(deadlineIso: string, today: Date = new Date()): boolean {
  const deadline = new Date(deadlineIso);
  return today.getTime() > deadline.getTime();
}

export function isAmendmentsOpen(amendmentsCloseIso: string, today: Date = new Date()): boolean {
  const close = new Date(amendmentsCloseIso);
  close.setHours(23, 59, 59, 999);
  return today.getTime() <= close.getTime();
}

export function physicalExamValidFrom(sessionStartIso: string): Date {
  const sessionStart = new Date(sessionStartIso);
  const twelveMonthsBefore = new Date(sessionStart);
  twelveMonthsBefore.setFullYear(twelveMonthsBefore.getFullYear() - 1);
  return twelveMonthsBefore;
}

// Date-only ISO strings ("YYYY-MM-DD") parse as UTC midnight. Reading them with
// local getters shifts the day backward in US time zones, so all formatting here
// pins to UTC to keep the displayed day matching the configured date.
export function formatSessionDateRange(startIso: string, endIso: string): string {
  const start = new Date(startIso);
  const end = new Date(endIso);
  const fmt = new Intl.DateTimeFormat("en-US", { month: "long", day: "numeric", timeZone: "UTC" });
  const sameMonth =
    start.getUTCMonth() === end.getUTCMonth() && start.getUTCFullYear() === end.getUTCFullYear();
  if (sameMonth) {
    const month = new Intl.DateTimeFormat("en-US", { month: "long", timeZone: "UTC" }).format(start);
    return `${month} ${start.getUTCDate()}–${end.getUTCDate()}, ${end.getUTCFullYear()}`;
  }
  return `${fmt.format(start)} – ${fmt.format(end)}, ${end.getUTCFullYear()}`;
}

// Computes age (whole years) on `asOfIso` for someone born on `dobIso`.
// Both are date-only ISO strings ("YYYY-MM-DD") parsed as UTC midnight, so we
// compare with UTC getters to avoid local-timezone day shifts. Returns null if
// either date is missing or invalid.
export function computeAgeAsOf(dobIso: string, asOfIso: string): number | null {
  if (!dobIso || !asOfIso) return null;
  const dob = new Date(dobIso);
  const asOf = new Date(asOfIso);
  if (Number.isNaN(dob.getTime()) || Number.isNaN(asOf.getTime())) return null;
  let age = asOf.getUTCFullYear() - dob.getUTCFullYear();
  const monthDelta = asOf.getUTCMonth() - dob.getUTCMonth();
  if (monthDelta < 0 || (monthDelta === 0 && asOf.getUTCDate() < dob.getUTCDate())) {
    age -= 1;
  }
  return age;
}

export function formatIsoDate(iso: string): string {
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  }).format(new Date(iso));
}
